import { CSSProperties, useEffect, useRef, useState } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { cn } from '../../../lib/utils';
import { SCREEN_LOADER_TOTAL_FRAMES } from './useMinLoadDuration';
import darkAnimation from './assets/SekretlyLoading3_dark.json';
import lightAnimation from './assets/SekretlyLoading3_light.json';

function getIsDark() {
  if (typeof document === 'undefined') return true;
  return document.documentElement.classList.contains('dark');
}

function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface ScreenLoaderProps {
  /** Pixel size of the mark. Default 96. */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * ScreenLoader — the branded Sekretly S-mark loading animation, for full
 * screen and page-level loads only (auth resolution, route transitions,
 * page data fetches that gate all of a page's content).
 *
 * For inline/button loading states, use `Spinner` instead — its CSS ring
 * takes `currentColor`, which this Lottie mark (fixed dark/light fills)
 * cannot do.
 */
export function ScreenLoader({ size = 96, className, style }: ScreenLoaderProps) {
  const [isDark, setIsDark] = useState(getIsDark);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.documentElement;
    const observer = new MutationObserver(() => setIsDark(getIsDark()));
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!reduced || !lottieRef.current) return;
    lottieRef.current.goToAndStop(SCREEN_LOADER_TOTAL_FRAMES - 1, true);
  }, [reduced]);

  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(className)}
      style={{ width: size, height: size, flexShrink: 0, ...style }}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={isDark ? darkAnimation : lightAnimation}
        loop
        autoplay={!reduced}
        style={{ width: '100%', height: '100%', background: 'transparent', opacity: 0.3 }}
      />
    </div>
  );
}

export default ScreenLoader;
